import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const UNIT_LABELS = ['bottle', 'box', 'tube', 'sachet', 'jar', 'pack', 'bag', 'each'];
const CATEGORIES = ['Supplement', 'Vitamin', 'Skincare', 'Beauty', 'Gift', 'Merchandise', 'Other'];

const emptyProduct = { name: '', owner_type: 'distributor', owner_id: null, unit_label: 'bottle', category: 'Supplement', non_sellable: false };
const emptyPackage = { name: '', price: '', effective_date: new Date().toISOString().slice(0, 10), status: 'active', line_items: [{ product_id: '', quantity: 1, is_foc: false }] };

export const ProductsPackagesPage = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [listingMatrix, setListingMatrix] = useState([]);

  const [productModal, setProductModal] = useState({ open: false, editing: null, form: emptyProduct });
  const [packageModal, setPackageModal] = useState({ open: false, editing: null, form: emptyPackage });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [p, pk, ph, pt, lm] = await Promise.all([
        api.get('/products'),
        api.get('/packages'),
        api.get('/pharmacies'),
        api.get('/partners'),
        api.get('/packages/listing-matrix')
      ]);
      setProducts(p.data);
      setPackages(pk.data);
      setPharmacies(ph.data);
      setPartners(pt.data);
      setListingMatrix(lm.data);
    } catch (e) { console.error(e); }
  };

  // -------- Product Dialog --------
  const openNewProduct = () => setProductModal({ open: true, editing: null, form: emptyProduct });
  const openEditProduct = (p) => setProductModal({
    open: true, editing: p.id,
    form: {
      name: p.name, owner_type: p.owner_type, owner_id: p.owner_id || null,
      unit_label: p.unit_label || 'bottle', category: p.category || 'Supplement',
      non_sellable: !!p.non_sellable
    }
  });
  const saveProduct = async () => {
    const f = productModal.form;
    if (!f.name.trim()) return toast.error('Product name is required');
    if (f.owner_type === 'partner' && !f.owner_id) return toast.error('Please select a partner as the owner');
    const payload = { ...f, owner_id: f.owner_type === 'partner' ? f.owner_id : null };
    try {
      if (productModal.editing) {
        await api.put(`/products/${productModal.editing}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      setProductModal({ open: false, editing: null, form: emptyProduct });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleProductStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`Set "${p.name}" to ${next.toUpperCase()}?`)) return;
    try {
      await api.put(`/products/${p.id}`, { status: next });
      toast.success(`Product ${next}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // -------- Package Dialog --------
  const openNewPackage = () => setPackageModal({ open: true, editing: null, form: emptyPackage });
  const openEditPackage = (pkg) => setPackageModal({
    open: true, editing: pkg.id,
    form: {
      name: pkg.name, price: pkg.price,
      effective_date: (pkg.effective_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
      status: pkg.status || 'active',
      line_items: pkg.line_items?.length ? pkg.line_items.map(li => ({ product_id: li.product_id, quantity: li.quantity, is_foc: !!li.is_foc })) : [{ product_id: '', quantity: 1, is_foc: false }]
    }
  });
  const updateLine = (idx, field, val) => {
    const items = [...packageModal.form.line_items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' ? parseInt(val) || 1 : (field === 'is_foc' ? !!val : val) };
    setPackageModal({ ...packageModal, form: { ...packageModal.form, line_items: items } });
  };
  const addLine = () => setPackageModal({ ...packageModal, form: { ...packageModal.form, line_items: [...packageModal.form.line_items, { product_id: '', quantity: 1, is_foc: false }] } });
  const removeLine = (idx) => setPackageModal({ ...packageModal, form: { ...packageModal.form, line_items: packageModal.form.line_items.filter((_, i) => i !== idx) } });

  const savePackage = async () => {
    const f = packageModal.form;
    if (!f.name.trim()) return toast.error('Package name is required');
    const price = parseFloat(f.price);
    if (isNaN(price) || price < 0) return toast.error('Price must be a non-negative number');
    if (!f.line_items.length || f.line_items.some(li => !li.product_id || !li.quantity)) return toast.error('Every line item needs a product and quantity');
    const payload = {
      name: f.name.trim(),
      price,
      line_items: f.line_items.map(li => ({ product_id: li.product_id, quantity: parseInt(li.quantity), is_foc: !!li.is_foc })),
      effective_date: new Date(f.effective_date).toISOString(),
    };
    try {
      if (packageModal.editing) {
        await api.put(`/packages/${packageModal.editing}`, { ...payload, status: f.status });
        toast.success('Package updated — past settlements are unaffected; new sales use the new price/config.');
      } else {
        await api.post('/packages', payload);
        toast.success('Package created');
      }
      setPackageModal({ open: false, editing: null, form: emptyPackage });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const togglePackageStatus = async (pkg) => {
    const next = pkg.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`Set "${pkg.name}" to ${next.toUpperCase()}?`)) return;
    try {
      await api.put(`/packages/${pkg.id}`, { status: next });
      toast.success(`Package ${next}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // -------- Listing Matrix --------
  const isListed = (packageId, pharmacyId) => listingMatrix.some(item => item.package_id === packageId && item.pharmacy_id === pharmacyId);
  const toggleListing = async (packageId, pharmacyId) => {
    try {
      const enabled = !isListed(packageId, pharmacyId);
      await api.post('/packages/listing-matrix', { package_id: packageId, pharmacy_id: pharmacyId, enabled });
      toast.success('Listing updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.products')}</h1>

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-listing-matrix">Listing Matrix</TabsTrigger>
        </TabsList>

        {/* ============ PRODUCTS TAB ============ */}
        <TabsContent value="products">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-product-btn" onClick={openNewProduct} style={{ backgroundColor: '#1E3A8A' }}>
              <Plus size={16} className="mr-2" /> New Product
            </Button>
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Unit</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`prod-row-${p.id}`} className="border-t border-slate-200">
                    <td className="p-3 font-medium">{p.name}</td>
                    <td className="p-3">{p.owner_name || (p.owner_type === 'partner' ? 'Partner' : 'OctoBoots Distribution')}</td>
                    <td className="p-3 text-slate-600">{p.category || '-'}</td>
                    <td className="p-3">{p.unit_label}</td>
                    <td className="p-3">
                      {p.non_sellable ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Non-sellable (Gift)</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Sellable</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {(p.status || 'active').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <Button data-testid={`edit-prod-${p.id}`} onClick={() => openEditProduct(p)} size="sm" variant="outline" className="mr-2">
                        <PencilSimple size={12} className="mr-1" /> Edit
                      </Button>
                      <Button data-testid={`toggle-prod-${p.id}`} onClick={() => toggleProductStatus(p)} size="sm" variant="outline">
                        {p.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan="7" className="p-8 text-center text-slate-500">No products yet — click <b>New Product</b> to add one.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ============ PACKAGES TAB ============ */}
        <TabsContent value="packages">
          <div className="flex justify-end mb-3">
            <Button data-testid="new-package-btn" onClick={openNewPackage} style={{ backgroundColor: '#1E3A8A' }}>
              <Plus size={16} className="mr-2" /> New Package
            </Button>
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-right p-3">Price (RM)</th>
                  <th className="text-left p-3">Line Items</th>
                  <th className="text-left p-3">Effective Date</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <tr key={pkg.id} data-testid={`pkg-row-${pkg.id}`} className="border-t border-slate-200">
                    <td className="p-3 font-medium">{pkg.name}</td>
                    <td className="p-3 text-right font-bold">{Number(pkg.price).toFixed(2)}</td>
                    <td className="p-3 text-xs text-slate-600">
                      {pkg.line_items?.map((li, i) => {
                        const prod = products.find(p => p.id === li.product_id);
                        return <div key={i}>{prod?.name || li.product_id} × {li.quantity} {li.is_foc && <span className="text-orange-600 font-semibold">(FOC)</span>}</div>;
                      })}
                    </td>
                    <td className="p-3 text-slate-600">{new Date(pkg.effective_date).toLocaleDateString()}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${pkg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{(pkg.status || 'active').toUpperCase()}</span>
                    </td>
                    <td className="p-3 text-right">
                      <Button data-testid={`edit-pkg-${pkg.id}`} onClick={() => openEditPackage(pkg)} size="sm" variant="outline" className="mr-2">
                        <PencilSimple size={12} className="mr-1" /> Edit
                      </Button>
                      <Button data-testid={`toggle-pkg-${pkg.id}`} onClick={() => togglePackageStatus(pkg)} size="sm" variant="outline">
                        {pkg.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {packages.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-slate-500">No packages yet — click <b>New Package</b> to create one.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* ============ LISTING MATRIX TAB ============ */}
        <TabsContent value="matrix">
          <Card className="overflow-x-auto">
            <p className="p-4 text-sm text-slate-600">Check the boxes to enable a package for a specific pharmacy. Changes are instant.</p>
            {packages.length === 0 || pharmacies.length === 0 ? (
              <p className="p-8 text-center text-slate-500">Add at least one package and one pharmacy before configuring the matrix.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-3">Package</th>
                    {pharmacies.map(ph => <th key={ph.id} className="text-center p-3 text-xs">{ph.name.slice(0, 15)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr key={pkg.id} className="border-t border-slate-200">
                      <td className="p-3 font-medium">{pkg.name}</td>
                      {pharmacies.map(ph => (
                        <td key={ph.id} className="p-3 text-center">
                          <Checkbox data-testid={`matrix-${pkg.id}-${ph.id}`} checked={isListed(pkg.id, ph.id)} onCheckedChange={() => toggleListing(pkg.id, ph.id)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ PRODUCT DIALOG ============ */}
      <Dialog open={productModal.open} onOpenChange={(o) => setProductModal({ ...productModal, open: o })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{productModal.editing ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input data-testid="prod-name" value={productModal.form.name} onChange={e => setProductModal({ ...productModal, form: { ...productModal.form, name: e.target.value } })} />
            </div>
            <div>
              <Label>Owner *</Label>
              <Select
                value={productModal.form.owner_type === 'distributor' ? '__distributor__' : (productModal.form.owner_id || '')}
                onValueChange={(v) => {
                  if (v === '__distributor__') setProductModal({ ...productModal, form: { ...productModal.form, owner_type: 'distributor', owner_id: null } });
                  else setProductModal({ ...productModal, form: { ...productModal.form, owner_type: 'partner', owner_id: v } });
                }}
              >
                <SelectTrigger data-testid="prod-owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__distributor__">OctoBoots Distribution (distributor)</SelectItem>
                  {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name} (partner)</SelectItem>)}
                </SelectContent>
              </Select>
              {productModal.form.owner_type === 'partner' && partners.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">No partners exist yet. Create a partner first on the Partners page.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Label</Label>
                <Select value={productModal.form.unit_label} onValueChange={v => setProductModal({ ...productModal, form: { ...productModal.form, unit_label: v } })}>
                  <SelectTrigger data-testid="prod-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIT_LABELS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={productModal.form.category} onValueChange={v => setProductModal({ ...productModal, form: { ...productModal.form, category: v } })}>
                  <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox data-testid="prod-nonsellable" checked={productModal.form.non_sellable} onCheckedChange={v => setProductModal({ ...productModal, form: { ...productModal.form, non_sellable: !!v } })} />
              <span className="text-sm">Non-sellable (gift / freebie — never earns revenue)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductModal({ open: false, editing: null, form: emptyProduct })}>Cancel</Button>
            <Button data-testid="save-prod-btn" onClick={saveProduct} style={{ backgroundColor: '#1E3A8A' }}>{productModal.editing ? 'Save Changes' : 'Create Product'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ PACKAGE DIALOG ============ */}
      <Dialog open={packageModal.open} onOpenChange={(o) => setPackageModal({ ...packageModal, open: o })}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{packageModal.editing ? 'Edit Package' : 'New Package'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Package Name *</Label>
                <Input data-testid="pkg-name" value={packageModal.form.name} onChange={e => setPackageModal({ ...packageModal, form: { ...packageModal.form, name: e.target.value } })} />
              </div>
              <div>
                <Label>Price (RM) *</Label>
                <Input data-testid="pkg-price" type="number" step="0.01" value={packageModal.form.price} onChange={e => setPackageModal({ ...packageModal, form: { ...packageModal.form, price: e.target.value } })} />
              </div>
              <div>
                <Label>Effective Date *</Label>
                <Input data-testid="pkg-effective-date" type="date" value={packageModal.form.effective_date} onChange={e => setPackageModal({ ...packageModal, form: { ...packageModal.form, effective_date: e.target.value } })} />
                <p className="text-xs text-slate-500 mt-1">Past settlements are never altered; new price applies from this date forward.</p>
              </div>
              {packageModal.editing && (
                <div>
                  <Label>Status</Label>
                  <Select value={packageModal.form.status} onValueChange={v => setPackageModal({ ...packageModal, form: { ...packageModal.form, status: v } })}>
                    <SelectTrigger data-testid="pkg-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label className="mb-2 block">Line Items *</Label>
              {products.length === 0 && (
                <p className="text-xs text-orange-600 mb-2">You need at least one product before creating a package.</p>
              )}
              <div className="space-y-2">
                {packageModal.form.line_items.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-slate-200 rounded p-2">
                    <div className="col-span-5">
                      <Label className="text-xs">Product</Label>
                      <Select value={li.product_id} onValueChange={v => updateLine(idx, 'product_id', v)}>
                        <SelectTrigger data-testid={`pkg-li-prod-${idx}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.non_sellable ? ' (gift)' : ''}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input data-testid={`pkg-li-qty-${idx}`} type="number" min="1" value={li.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Type</Label>
                      <Select value={li.is_foc ? 'foc' : 'paid'} onValueChange={v => updateLine(idx, 'is_foc', v === 'foc')}>
                        <SelectTrigger data-testid={`pkg-li-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="foc">FOC (free)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      {packageModal.form.line_items.length > 1 && (
                        <Button size="sm" variant="outline" data-testid={`pkg-li-remove-${idx}`} onClick={() => removeLine(idx)}><Trash size={14} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="pkg-add-line" className="mt-2">
                <Plus size={12} className="mr-1" /> Add line item
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackageModal({ open: false, editing: null, form: emptyPackage })}>Cancel</Button>
            <Button data-testid="save-pkg-btn" onClick={savePackage} style={{ backgroundColor: '#1E3A8A' }}>{packageModal.editing ? 'Save Changes' : 'Create Package'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
